import { BRIDGE_ERROR, BRIDGE_TIMEOUT_MS } from '../../constants/bridge';
import { fail } from '../result';
import { validateArgs, validateValue } from '../schema';
import { buildCancel, buildRequest, normalizeOrigin, parseResponse } from './protocol';

const defaultId = () => crypto.randomUUID();

// Embedded-app side of the postMessage bridge. The message listener is only
// attached while at least one request is pending, so an idle bridge holds no
// listeners and nothing leaks when the component tree unmounts.
export function createPostMessageClient({
    win = window,
    target = win.parent,
    targetOrigin,
    timeoutMs = BRIDGE_TIMEOUT_MS.DEFAULT,
    createId = defaultId,
    onRejected,
} = {}) {
    const origin = normalizeOrigin(targetOrigin);
    if (!origin) throw new Error('[bridge] postMessage client requires an explicit http(s) target origin');
    if (!target || target === win) throw new Error('[bridge] postMessage client requires a distinct target window');

    const pending = new Map();
    let isListening = false;
    let isDisposed = false;

    const reject = (reason, detail) => onRejected?.(reason, detail);

    function handleMessage(event) {
        if (isDisposed) return;
        if (event.origin !== origin) return reject('origin', event.origin);
        if (event.source !== target) return reject('source');
        const parsed = parseResponse(event.data);
        if (!parsed) return reject('envelope');
        const entry = pending.get(parsed.id);
        // Unknown ids cover both foreign responses and duplicates: an id is
        // removed from `pending` the first time it settles.
        if (!entry) return reject('unknown-id', parsed.id);

        if (parsed.result.ok) {
            const checked = validateValue(entry.method, parsed.result.value);
            settle(parsed.id, checked.valid
                ? { ok: true, value: checked.value }
                : fail(BRIDGE_ERROR.INVALID_RESPONSE));
            return;
        }
        settle(parsed.id, parsed.result);
    }

    function listen() {
        if (isListening) return;
        win.addEventListener('message', handleMessage);
        isListening = true;
    }

    function unlistenIfIdle() {
        if (!isListening || pending.size > 0) return;
        win.removeEventListener('message', handleMessage);
        isListening = false;
    }

    function settle(id, result) {
        const entry = pending.get(id);
        if (!entry) return;
        pending.delete(id);
        clearTimeout(entry.timer);
        entry.detachAbort();
        unlistenIfIdle();
        entry.resolve(result);
    }

    function sendCancel(id) {
        try {
            target.postMessage(buildCancel(id), origin);
        } catch {
            // The host may already be gone; the local promise is settled regardless.
        }
    }

    function request(method, payload = {}, { signal, timeoutMs: requestTimeout } = {}) {
        if (isDisposed) return Promise.resolve(fail(BRIDGE_ERROR.DISPOSED));
        const checked = validateArgs(method, payload);
        if (!checked.valid) return Promise.resolve(fail(BRIDGE_ERROR.INVALID_ARGUMENT, checked.reason));
        if (signal?.aborted) return Promise.resolve(fail(BRIDGE_ERROR.CANCELLED));

        return new Promise((resolve) => {
            const id = createId();
            const handleAbort = () => {
                sendCancel(id);
                settle(id, fail(BRIDGE_ERROR.CANCELLED));
            };
            const timer = setTimeout(() => {
                sendCancel(id);
                settle(id, fail(BRIDGE_ERROR.TIMEOUT));
            }, requestTimeout ?? timeoutMs);

            signal?.addEventListener('abort', handleAbort, { once: true });
            pending.set(id, {
                method,
                resolve,
                timer,
                detachAbort: () => signal?.removeEventListener('abort', handleAbort),
            });
            listen();

            try {
                target.postMessage(buildRequest(id, method, checked.value), origin);
            } catch {
                settle(id, fail(BRIDGE_ERROR.FAILED));
            }
        });
    }

    function dispose() {
        isDisposed = true;
        for (const id of [...pending.keys()]) {
            sendCancel(id);
            settle(id, fail(BRIDGE_ERROR.DISPOSED));
        }
        if (isListening) {
            win.removeEventListener('message', handleMessage);
            isListening = false;
        }
    }

    return {
        request,
        dispose,
        get pendingCount() { return pending.size; },
        get isListening() { return isListening; },
    };
}
